import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { API_ENDPOINTS } from '../config/api'
import { getActiveOrgId, setActiveOrgId } from '../utils/orgUtils'
import { USE_MOCK_API } from '../mocks/useMockApi'
import { MOCK_AUTH } from '../mocks/mockAuth'

interface User {
  id: string
  username: string
  email: string
  role: string
  permissions: string[]
  organization?: string
  organizations?: string[]
  organizationIds?: string[]
  organizationsDetailed?: Array<{ id: string; name?: string }>
  fullName?: string
  phone?: string
  position?: string
}

/** Normalize role/permissions so ADMIN/admin and JSON perms compare consistently in UI. */
function normalizeAuthUser(raw: User | Record<string, unknown> | null | undefined): User | null {
  if (!raw || typeof raw !== 'object') return null
  const role = String((raw as User).role ?? 'user').toLowerCase()
  let permissions: string[] = []
  const rawPerms = (raw as User).permissions
  if (Array.isArray(rawPerms)) {
    permissions = rawPerms.map(String)
  } else if (typeof rawPerms === 'string') {
    try {
      const parsed = JSON.parse(rawPerms)
      permissions = Array.isArray(parsed) ? parsed.map(String) : []
    } catch {
      permissions = []
    }
  }
  return { ...(raw as User), role, permissions }
}

interface AuthContextType {
  user: User | null
  token: string | null
  login: (token: string, user: User) => void
  logout: () => void
  isAuthenticated: boolean
  initialized: boolean
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() =>
    USE_MOCK_API ? ({ ...MOCK_AUTH.user } as User) : null
  )
  const [token, setToken] = useState<string | null>(() => (USE_MOCK_API ? MOCK_AUTH.token : null))
  const [isAuthenticated, setIsAuthenticated] = useState(() => USE_MOCK_API)
  const [initialized, setInitialized] = useState(() => USE_MOCK_API)

  useEffect(() => {
    if (USE_MOCK_API) {
      localStorage.setItem('token', MOCK_AUTH.token)
      localStorage.setItem('user', JSON.stringify(MOCK_AUTH.user))
      return
    }

    const storedToken = localStorage.getItem('token')
    const storedUser = localStorage.getItem('user')

    if (storedToken && storedUser) {
      const parsedUser = normalizeAuthUser(JSON.parse(storedUser))
      console.log('AuthProvider - Initialization user:', parsedUser)
      console.log('AuthProvider - Initialization organizations:', parsedUser?.organizations)
      setToken(storedToken)
      setUser(parsedUser)
      setIsAuthenticated(true)
    }
    setInitialized(true)
  }, [])

  const login = (newToken: string, newUser: User) => {
    const normalized = normalizeAuthUser(newUser) || newUser
    console.log('AuthProvider - Login user:', normalized)
    console.log('AuthProvider - User organizations:', normalized.organizations)
    setToken(newToken)
    setUser(normalized)
    setIsAuthenticated(true)
    localStorage.setItem('token', newToken)
    localStorage.setItem('user', JSON.stringify(normalized))
    setActiveOrgId(getActiveOrgId())
  }

  const logout = () => {
    setToken(null)
    setUser(null)
    setIsAuthenticated(false)
    setActiveOrgId(null)
    localStorage.removeItem('token')
    localStorage.removeItem('user')
  }

  const refreshUser = async () => {
    if (USE_MOCK_API) return
    try {
      const authToken = localStorage.getItem('token')
      if (!authToken) return
      const res = await fetch(API_ENDPOINTS.ME, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      })
      if (res.ok) {
        const data = await res.json()
        const userData = data.user ?? (data?.id ? data : null)
        console.log('AuthProvider - Refresh user data:', userData)
        console.log('AuthProvider - Refresh user organizations:', userData?.organizations)
        if (userData) {
          const normalized = normalizeAuthUser(userData as User)
          setUser(normalized)
          localStorage.setItem('user', JSON.stringify(normalized))
          setIsAuthenticated(true)
        }
      }
    } catch (error) {
      console.error('AuthProvider - Refresh error:', error)
    }
  }

  const value = {
    user,
    token,
    login,
    logout,
    isAuthenticated,
    initialized,
    refreshUser
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}